import { Map, List } from 'immutable';
import React from 'react';

// icons
import AccountBalance from '@material-ui/icons/AccountBalance';
import AccountIcon from '@material-ui/icons/SettingsApplications';
import BillingIcon from '@material-ui/icons/Payment';
import FieldsIcon from '@material-ui/icons/List';
import IntegrationsIcon from '@material-ui/icons/Code';
import LocalOffer from '@material-ui/icons/LocalOffer';
import MembersIcon from '@material-ui/icons/Group';
import MilestonesIcon from '@material-ui/icons/LinearScale';
import PhoneIcon from '@material-ui/icons/PermPhoneMsg';
import StatusIcon from '@material-ui/icons/SwapVert';
import TagsIcon from '@material-ui/icons/LocalOffer';
import ValuesIcon from '@material-ui/icons/Star';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

// routes
import AccountSettings from 'routes/AccountSettings';
import AdminSettings from 'routes/AdminSettings';
import BillingSettings from 'routes/BillingSettings';
import FieldsSettings from 'routes/FieldsSettings';
import GroupSettings from 'routes/GroupSettings';
import IntegrationsSettings from 'routes/IntegrationsSettings';
import LabelsSettings from 'routes/LabelsSettings';
import MemberSettings from 'routes/MemberSettings';
import MilestonesSettings from 'routes/MilestonesSettings';
import PhoneSettings from 'routes/PhoneSettings';
import StatusSettings from 'routes/StatusSettings';
import TagsSettings from 'routes/TagsSettings';
import ValuesSettings from 'routes/ValuesSettings';
import PaymentSettings from 'routes/PaymentSettings';
import CommunitySettings from 'routes/CommunitySettings';

interface SettingsTab {
  label: string;
  icon: React.ReactNode;
  route: string;
  component: React.ComponentType<any> | (() => JSX.Element[]);
}

type ImmutableSettingsTab = Map<keyof SettingsTab, any>;

interface GetSettingsTabsParams {
  isSystemAdmin?: boolean;
  isCurrentGroupSet?: boolean;
  features?: Map<any, any>;
  currentUserRole?: number;
  isCurrentGroupChapter?: boolean;
  isCurrentGroupOrganization?: boolean;
  isCurrentGroupCouncil?: boolean;
  isCurrentGroupSystem?: boolean;
}

export default function getSettingsTabs({
  isSystemAdmin,
  isCurrentGroupSet,
  features = Map(),
  currentUserRole,
  isCurrentGroupChapter,
  isCurrentGroupOrganization,
  isCurrentGroupCouncil,
  isCurrentGroupSystem,
}: GetSettingsTabsParams): List<ImmutableSettingsTab> {
  const tabs: SettingsTab[] = [
    {
      label: 'Account',
      icon: <AccountIcon />,
      route: '/settings/account',
      component: AccountSettings,
    },
  ];

  if (isSystemAdmin) {
    tabs.push(
      {
        label: 'Admins',
        icon: <MembersIcon />,
        route: '/settings/admins',
        component: AdminSettings,
      },
      {
        label: 'Integrations',
        icon: <IntegrationsIcon />,
        route: '/settings/integrations',
        component: IntegrationsSettings,
      }
    );
  }

  if (
    isCurrentGroupSet &&
    !isCurrentGroupSystem &&
    (currentUserRole === 1 || isSystemAdmin)
  ) {
    tabs.push({
      label: 'Members',
      icon: <MembersIcon />,
      route: '/settings/members',
      component: MemberSettings,
    });
  }

  if (isCurrentGroupOrganization || isCurrentGroupCouncil) {
    tabs.push({
      label: 'Chapter Labels',
      icon: <LocalOffer />,
      route: '/settings/labels',
      component: LabelsSettings,
    });
  }

  if (isCurrentGroupCouncil) {
    tabs.push(
      {
        label: 'Community',
        icon: <AccountBalanceIcon />,
        route: '/settings/community',
        component: CommunitySettings,
      },
      {
        label: 'Lead Tags',
        icon: <TagsIcon />,
        route: '/settings/tags',
        component: TagsSettings,
      },
      {
        label: 'Fields',
        icon: <FieldsIcon />,
        route: '/settings/fields',
        component: FieldsSettings,
      },
      {
        label: 'Payments',
        icon: <MonetizationOnOutlinedIcon />,
        route: '/settings/payments',
        component: PaymentSettings,
      }
    );
  }

  if (
    (isCurrentGroupChapter || isCurrentGroupCouncil) &&
    isCurrentGroupSet &&
    (currentUserRole === 1 || isSystemAdmin)
  ) {
    if (!isCurrentGroupCouncil) {
      tabs.push(
        {
          label: 'Organization',
          icon: <AccountBalance />,
          route: '/settings/group',
          component: GroupSettings,
        },
        {
          label: 'Billing',
          icon: <BillingIcon />,
          route: '/settings/billing',
          component: BillingSettings,
        }
      );
    }

    if (
      (isCurrentGroupChapter || isCurrentGroupCouncil) &&
      features.getIn(['phone_numbers', 'enabled'], isSystemAdmin)
    ) {
      tabs.push({
        label: 'Phone',
        icon: <PhoneIcon />,
        route: '/settings/phone',
        component: PhoneSettings,
      });
    }

    if (
      (isCurrentGroupChapter || isCurrentGroupCouncil) &&
      features.getIn(['custom_statuses', 'enabled'], isSystemAdmin)
    ) {
      tabs.push({
        label: 'Status',
        icon: <StatusIcon />,
        route: '/settings/statuses',
        component: StatusSettings,
      });
    }
    if (
      isCurrentGroupChapter &&
      features.getIn(['custom_milestones', 'enabled'], isSystemAdmin)
    ) {
      tabs.push({
        label: 'Milestones',
        icon: <MilestonesIcon />,
        route: '/settings/milestones',
        component: MilestonesSettings,
      });
    }
    if (
      isCurrentGroupChapter &&
      features.getIn(['custom_tags', 'enabled'], isSystemAdmin)
    ) {
      tabs.push({
        label: 'Tags',
        icon: <TagsIcon />,
        route: '/settings/tags',
        component: TagsSettings,
      });
    }
    if (
      isCurrentGroupChapter &&
      features.getIn(['pnm_attrs', 'enabled'], isSystemAdmin)
    ) {
      tabs.push({
        label: 'Fields',
        icon: <FieldsIcon />,
        route: '/settings/fields',
        component: FieldsSettings,
      });
    }
    if (isCurrentGroupChapter && features.getIn(['values', 'enabled'], false)) {
      tabs.push({
        label: 'Values',
        icon: <ValuesIcon />,
        route: '/settings/values',
        component: ValuesSettings,
      });
    }
  }

  return List(tabs.map(Map)) as List<ImmutableSettingsTab>;
}
